import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { TextField } from '@/components/TextField/TextField';
import { useAuthStore } from '@/stores/auth';
import { t } from '@/lib/i18n';
import { ApiError, NetworkError } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { login, resendVerification } from '../api';
import { loginFormSchema, type LoginFormValues } from '../schemas';
import styles from './AuthForm.module.css';

interface LoginFormProps {
  verified?: boolean;
}

export function LoginForm({ verified = false }: LoginFormProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [emailUnverified, setEmailUnverified] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: async () => {
      // Trigger the bootstrap re-fetch — useBootstrapAuth's effect will
      // setSession once /me lands. We CAN'T navigate here directly: the
      // store update happens in that effect on a subsequent render cycle,
      // so navigating now would render RequireAuth against the still-stale
      // store and bounce the user back to /login?returnTo=/. The useEffect
      // below waits for isAuthenticated to flip true, then navigates.
      setSubmitted(true);
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'EMAIL_NOT_VERIFIED') {
        setEmailUnverified(true);
      } else if (err instanceof ApiError && err.status === 401) {
        setFormError(t('Invalid email or password.'));
      } else if (err instanceof NetworkError) {
        setFormError(t("Couldn't reach the server. Check your connection and try again."));
      } else {
        setFormError(t('Something went wrong. Please try again.'));
      }
    },
  });

  // Navigate once the auth store has actually been populated by the
  // bootstrap effect. Guarded by `submitted` so users who land on /login
  // while already signed in elsewhere don't get bounced before they can
  // interact with the form.
  useEffect(() => {
    if (submitted && isAuthenticated) {
      const returnTo = searchParams.get('returnTo') ?? '/';
      void navigate(returnTo, { replace: true });
    }
  }, [submitted, isAuthenticated, searchParams, navigate]);

  function onSubmit(values: LoginFormValues) {
    setEmailUnverified(false);
    setResendSent(false);
    setFormError(null);
    mutation.mutate(values);
  }

  async function handleResend() {
    const email = form.getValues('email');
    if (!email) return;
    await resendVerification(email);
    setResendSent(true);
  }

  return (
    <form className={styles.form} onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} noValidate>
      {verified ? (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            background: 'var(--color-success-bg)',
            border: '1px solid var(--color-success)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--color-success)',
            marginBottom: 4,
          }}
        >
          {t('Your email is confirmed — sign in to meet your team.')}
        </div>
      ) : null}
      <TextField
        label={t('Email')}
        type="email"
        autoComplete="email"
        placeholder="you@example.com"
        error={form.formState.errors.email?.message}
        {...form.register('email')}
      />
      <TextField
        label={t('Password')}
        type="password"
        autoComplete="current-password"
        error={form.formState.errors.password?.message}
        {...form.register('password')}
      />

      {emailUnverified ? (
        <div
          role="alert"
          style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.5 }}
        >
          {t('Please verify your email before signing in.')}{' '}
          {resendSent ? (
            <span style={{ color: 'var(--color-success)' }}>{t('New link sent!')}</span>
          ) : (
            <button
              type="button"
              onClick={() => void handleResend()}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--color-action)',
                cursor: 'pointer',
                fontSize: 'inherit',
                textDecoration: 'underline',
              }}
            >
              {t('Request a new confirmation link →')}
            </button>
          )}
        </div>
      ) : null}

      {formError ? (
        <div className={styles.formError} role="alert">
          {formError}
        </div>
      ) : null}

      <Button type="submit" variant="primary" size="lg" isLoading={mutation.isPending}>
        {mutation.isPending ? t('Signing in…') : t('Sign in')}
      </Button>

      <p className={styles.alt}>
        {t('New to Linqage?')}{' '}
        <Link to="/signup" className={styles.altLink}>
          {t('Create a workspace')}
        </Link>
      </p>
    </form>
  );
}
